import React, { useEffect, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import Icon from './AppIcon';
import Button from './ui/Button';

/**
 * CelebrationModal — full-screen overlay for badge unlocks, streak milestones, and play completions.
 *
 * Props:
 *   celebration: { type: 'badge'|'streak'|'play_complete', badge?, streakDays?, playTitle? } | null
 *   onDismiss: () => void
 */

const STREAK_MESSAGES = {
  3:  { emoji: '🔥', title: '3-Day Streak!',  message: "You\'re on fire! Three days in a row — keep the momentum going!" },
  7:  { emoji: '⚡', title: 'One Week Streak!', message: "A full week of Shakespeare! Your dedication is truly impressive." },
  14: { emoji: '🌟', title: 'Two-Week Streak!', message: "Fourteen days strong! You\'re becoming a true Bard scholar." },
  30: { emoji: '👑', title: '30-Day Streak!',  message: "A whole month! You've achieved what few dare attempt. Magnificent!" },
};

const getStreakInfo = (days) => {
  // Return the highest matching milestone message
  const milestones = [30, 14, 7, 3];
  for (const m of milestones) {
    if (days >= m) return STREAK_MESSAGES?.[m];
  }
  return { emoji: '🔥', title: `${days}-Day Streak!`, message: "Keep up the great work!" };
};

const CelebrationModal = ({ celebration, onDismiss }) => {
  const firedRef = useRef(false);

  const fireConfetti = useCallback((type) => {
    if (firedRef?.current) return;
    firedRef.current = true;

    // Gold / burgundy / purple palette
    const colors =
      type === 'play_complete'
        ? ['#D4AF37', '#FFD700', '#FFF8DC']
        : ['#D4AF37', '#800020', '#6B21A8', '#FFD700', '#C084FC'];

    if (type === 'play_complete') {
      // Short burst for play completion
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.5 },
        colors,
        startVelocity: 35,
        gravity: 1.2,
        ticks: 150,
      });
    } else {
      // Big burst for badge / streak
      const burst = (opts) =>
        confetti({
          particleCount: 60,
          spread: 80,
          colors,
          startVelocity: 45,
          gravity: 0.9,
          ticks: 200,
          ...opts,
        });

      burst({ origin: { x: 0.25, y: 0.55 } });
      setTimeout(() => burst({ origin: { x: 0.75, y: 0.55 } }), 150);
      setTimeout(() => burst({ origin: { x: 0.5, y: 0.4 } }), 300);
    }
  }, []);

  useEffect(() => {
    if (!celebration) {
      firedRef.current = false;
      return;
    }
    // Small delay so the modal renders first
    const t = setTimeout(() => fireConfetti(celebration?.type), 120);
    return () => clearTimeout(t);
  }, [celebration, fireConfetti]);

  if (!celebration) return null;

  const { type, badge, streakDays, playTitle } = celebration;

  // ── Badge unlock ──────────────────────────────────────────────────────────
  if (type === 'badge') {
    return (
      <Overlay onDismiss={onDismiss}>
        <div className="flex flex-col items-center text-center gap-5">
          {/* Badge icon ring */}
          <div className="relative">
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-yellow-400 via-amber-500 to-yellow-600 flex items-center justify-center shadow-2xl animate-bounce-slow">
              <Icon name="Award" size={56} color="#fff" />
            </div>
            <span className="absolute -top-2 -right-2 text-3xl animate-spin-slow">✨</span>
          </div>

          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-amber-400 mb-1">Badge Unlocked!</p>
            <h2 className="text-3xl font-bold text-white mb-2">{badge?.name || 'New Badge'}</h2>
            {badge?.description && (
              <p className="text-base text-white/80 max-w-xs">{badge?.description}</p>
            )}
          </div>

          <Button variant="default" size="lg" onClick={onDismiss} className="mt-2 min-w-[160px]">
            Awesome! 🎉
          </Button>
        </div>
      </Overlay>
    );
  }

  // ── Streak milestone ──────────────────────────────────────────────────────
  if (type === 'streak') {
    const info = getStreakInfo(streakDays);
    return (
      <Overlay onDismiss={onDismiss}>
        <div className="flex flex-col items-center text-center gap-5">
          {/* Flame animation */}
          <div className="relative">
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-orange-400 via-red-500 to-rose-600 flex items-center justify-center shadow-2xl">
              <span className="text-6xl animate-flame">{info?.emoji}</span>
            </div>
            <div className="absolute inset-0 rounded-full bg-orange-400/20 animate-ping" />
          </div>

          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-orange-300 mb-1">Milestone Reached!</p>
            <h2 className="text-3xl font-bold text-white mb-2">{info?.title}</h2>
            <p className="text-base text-white/80 max-w-xs">{info?.message}</p>
          </div>

          {/* Streak number badge */}
          <div className="flex items-center gap-2 bg-white/10 rounded-full px-5 py-2">
            <Icon name="Flame" size={20} color="#fb923c" />
            <span className="text-2xl font-bold text-white">{streakDays}</span>
            <span className="text-white/70 text-sm">days</span>
          </div>

          <Button variant="default" size="lg" onClick={onDismiss} className="mt-2 min-w-[160px]">
            Keep it up! 🔥
          </Button>
        </div>
      </Overlay>
    );
  }

  // ── Play complete ─────────────────────────────────────────────────────────
  if (type === 'play_complete') {
    return (
      <Overlay onDismiss={onDismiss} compact>
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center shadow-xl">
            <Icon name="CheckCircle" size={44} color="#fff" />
          </div>

          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-emerald-300 mb-1">Play Complete!</p>
            <h2 className="text-2xl font-bold text-white mb-1">{playTitle || 'Play Finished!'}</h2>
            <p className="text-sm text-white/75">You've read every line. Bravo! 🎭</p>
          </div>

          <Button variant="default" size="md" onClick={onDismiss} className="mt-1 min-w-[140px]">
            Continue 🎉
          </Button>
        </div>
      </Overlay>
    );
  }

  return null;
};

// ── Shared overlay wrapper ────────────────────────────────────────────────────
const Overlay = ({ children, onDismiss, compact = false }) => (
  <div
    className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
    style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
    onClick={onDismiss}
  >
    <div
      className={`relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-white/10 rounded-2xl shadow-2xl ${compact ? 'p-8 max-w-sm w-full' : 'p-10 max-w-md w-full'} animate-celebration-in`}
      onClick={(e) => e?.stopPropagation()}
    >
      {/* Close button */}
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 text-white/40 hover:text-white/80 transition-colors"
        aria-label="Dismiss"
      >
        <Icon name="X" size={20} />
      </button>
      {children}
    </div>
  </div>
);

export default CelebrationModal;
